import * as Sentry from "@sentry/react-native";
import { router } from "expo-router";
import { ParseError, parsePhoneNumberWithError } from "libphonenumber-js";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { StyleSheet } from "react-native";
import * as yup from "yup";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { Field } from "~/components/V2/Field";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import { useStore } from "~/mobx/utils/useStore";
import { axiosErrorSchema } from "~/schemas";
import { assert } from "~/utils/assert";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { EditAddressModal } from "../settings/EditAddressModal";
import { EditTextInputModal } from "../settings/EditTextInputModal";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import { withBookingData } from "./withBookingData";

const S = StyleSheet.create({
  logoutButton: { width: "100%" },

  container: {
    paddingHorizontal: 0,
  },
  subtitleContainer: { marginTop: 38 },
  subtitle: { fontSize: 20, lineHeight: 28 },
});

// Edit contact details screen inside book appointment flow
// This screen is similar to the settings screen but has a different header and cta button at the bottom
// so we decided to create a separate screen so we dont mix two different flows
// this way logic is more clear and we can easily change the layout of the screen
const EditContactDetailsScreen = observer(function EditContactDetailsScreen() {
  const { isStandAloneBookingFlow } = useStandaloneBookingFlow();
  const store = useStore();
  const errorAlert = useErrorAlertV2();
  const activeUser = store.authStore.activeUser;

  const [isEditAddressModalVisible, setIsEditAddressModalVisible] =
    useState(false);

  const [isEditMobilePhoneVisible, setIsEditMobilePhoneVisible] =
    useState(false);

  const [isEditEmailVisible, setIsEditEmailVisible] = useState(false);

  assert(activeUser, "Active user is not defined");

  return (
    <View flex colorWhite>
      <EditAddressModal
        isVisible={isEditAddressModalVisible}
        onClose={() => {
          setIsEditAddressModalVisible(false);
        }}
        user={activeUser}
      />

      <EditTextInputModal
        keyboardType="phone-pad"
        isVisible={isEditMobilePhoneVisible}
        onClose={() => {
          setIsEditMobilePhoneVisible(false);
        }}
        title={intl.formatMessage({
          id: "GAG4kM",
          defaultMessage: "Update mobile number",
          description: "Title for mobile phone input",
        })}
        onUpdate={async (value) => {
          try {
            await store.userStore.updateActiveUser({
              phoneNumber: value,
            });

            // Log the event to the booking data layer
            // if we are in the standalone booking flow
            if (isStandAloneBookingFlow) {
              bookingDataLayerPush({
                event: "phone_change_success_web_app",
              });
            }
          } catch (error) {
            // Log the event to the booking data layer
            // if we are in the standalone booking flow
            if (isStandAloneBookingFlow) {
              const errorData = axiosErrorSchema.safeParse(error);

              const errorCode = errorData.success
                ? errorData.data.response.data.error.code
                : error instanceof Error
                  ? error.message
                  : "unknown";

              bookingDataLayerPush({
                event: "phone_change_failed_web_app",
                error_code: errorCode,
              });
            }

            console.log("Error while updating mobile phone", error);
            errorAlert(error);
            Sentry.captureException(error);
          }
        }}
        placeholder={intl.formatMessage({
          id: "R2Y70i",
          defaultMessage: "Enter your mobile number",
          description: "Placeholder inside text input",
        })}
        initialValue={activeUser.phoneNumber}
        validationSchema={yup
          .string()
          .required()
          .test(
            "not-only-spaces",
            intl.formatMessage({
              id: "KBojtB",
              description: "Error message for empty phone number",
              defaultMessage: "Phone number is required",
            }),
            (value) => !!value && value.trim().length > 0,
          )
          .test("is-phone-number", (value, ctx) => {
            if (!!value && value.trim()) {
              try {
                parsePhoneNumberWithError(value, {
                  extract: false,
                });

                return true;
              } catch (error) {
                if (error instanceof ParseError) {
                  if (error.message === "TOO_LONG") {
                    return ctx.createError({
                      message: intl.formatMessage({
                        id: "pB8SjL",
                        description: "Error message for too long phone number",
                        defaultMessage: "The phone number is too long",
                      }),
                    });
                  } else if (error.message === "TOO_SHORT") {
                    return ctx.createError({
                      message: intl.formatMessage({
                        id: "l8lO/0",
                        description: "Error message for too short phone number",
                        defaultMessage: "The phone number is too short",
                      }),
                    });
                  } else {
                    return ctx.createError({
                      message: intl.formatMessage({
                        id: "N/1f86",
                        description: "Error message for invalid phone number",
                        defaultMessage:
                          "The phone number you entered is not valid",
                      }),
                    });
                  }
                } else {
                  errorAlert(error);
                  Sentry.captureException(error);
                  return ctx.createError({
                    message: intl.formatMessage({
                      id: "N/1f86",
                      description: "Error message for invalid phone number",
                      defaultMessage:
                        "The phone number you entered is not valid",
                    }),
                  });
                }
              }
            }
            return ctx.createError({
              message: intl.formatMessage({
                id: "KBojtB",
                description: "Error message for empty phone number",
                defaultMessage: "Phone number is required",
              }),
            });
          })}
      />

      <EditTextInputModal
        isVisible={isEditEmailVisible}
        onClose={() => {
          setIsEditEmailVisible(false);
        }}
        title={intl.formatMessage({
          id: "sFmNxA",
          defaultMessage: "Update e-mail address",
          description: "message",
        })}
        onUpdate={async (value) => {
          try {
            await store.userStore.updateActiveUser({
              email: value,
            });

            if (isStandAloneBookingFlow) {
              // Log the event to the booking data layer
              bookingDataLayerPush({
                event: "email_change_success_web_app",
              });
            }
          } catch (error) {
            if (isStandAloneBookingFlow) {
              const errorData = axiosErrorSchema.safeParse(error);

              const errorCode = errorData.success
                ? errorData.data.response.data.error.code
                : error instanceof Error
                  ? error.message
                  : "unknown";

              // Log the event to the booking data layer
              bookingDataLayerPush({
                event: "email_change_failed_web_app",
                error_code: errorCode,
              });
            }

            console.log("Error while updating email", error);
            errorAlert(error);
            Sentry.captureException(error);
          }
        }}
        placeholder={intl.formatMessage({
          id: "Lj4b+u",
          defaultMessage: "Enter your e-mail address",
          description: "Placeholder inside text input",
        })}
        initialValue={activeUser.email}
        validationSchema={yup.string().email()}
      />

      {/* Subtitle */}
      <View paddingHorizontal24 paddingVertical16 style={S.subtitleContainer}>
        <BrandText weightExtraBold colorBlack style={S.subtitle}>
          {intl.formatMessage({
            id: "a8I10Q",
            defaultMessage: "Contact details",
            description: "Title for contact details",
          })}
        </BrandText>
      </View>

      {/* Start form */}

      {/* Name */}
      <Field
        label={intl.formatMessage({
          id: "OMbXig",
          defaultMessage: "Name",
          description: "Label for the name input",
        })}
        value={activeUser.fullName}
      />

      {/* Address */}
      <Field
        onPress={() => {
          setIsEditAddressModalVisible(true);
        }}
        label={intl.formatMessage({
          id: "7Vk55F",
          defaultMessage: "Address",
          description: "Label for the address input",
        })}
        value={activeUser.fullAddress}
        isEditable
      />

      {/* Mobile */}
      <Field
        onPress={() => {
          setIsEditMobilePhoneVisible(true);
        }}
        label={intl.formatMessage({
          id: "VzXPY/",
          defaultMessage: "Mobile",
          description: "Label for the mobile input",
        })}
        value={activeUser.phoneNumber}
        isEditable
      />
      {/* E-mail */}
      <Field
        onPress={() => {
          setIsEditEmailVisible(true);
        }}
        label={intl.formatMessage({
          id: "P4eWiS",
          defaultMessage: "E-mail",
          description: "Label for the e-mail input",
        })}
        value={activeUser.email}
        isLast
        isEditable
      />

      {/* End form */}

      <Spacer extraExtraLarge />
      <Spacer large />
      {/* Confirm button */}
      <View
        alignItemsCenter
        paddingHorizontal24
        flexDirectionColumnReverse
        flex
        style={{ paddingBottom: 80 }}
      >
        <ButtonV2
          medium
          style={S.logoutButton}
          secondary
          title={intl.formatMessage({
            id: "wyrlLZ",
            defaultMessage: "Confirm",
            description: "Confirm editing contact details",
          })}
          onPress={() => {
            router.dismiss();
          }}
        />
      </View>
    </View>
  );
});

// If any required data from earlier in the booking process is missing, redirect to the first step
export const EditContactDetailsScreenWithBookingData = withBookingData(
  EditContactDetailsScreen,
  ["clinicId", "patientId", "reasonId", "practitionerId", "start", "end"],
);
